<template>
    <!-- QR scanner (front camera). Owns the camera for as long as it is open:
         the stream starts when open() shows the modal and stops the moment
         the modal hides, whichever way it hides. -->
    <b-modal
        :id="id"
        :title="$t('virtual_kiosk.scan_qr_title')"
        hide-footer
        centered
        @show="$emit('shown')"
        @hidden="onHidden"
    >
        <div class="scanner-frame">
            <video ref="qrVideo" autoplay playsinline muted></video>
            <span class="corner tl"></span>
            <span class="corner tr"></span>
            <span class="corner bl"></span>
            <span class="corner br"></span>
        </div>

        <div v-if="camera_error" class="alert alert-danger mt-3 mb-0">
            {{ camera_error }}
        </div>

        <p v-if="scan_notice" class="scanner-notice mt-3 mb-0">{{ scan_notice }}</p>
        <p class="scanner-instruction mt-3">{{ $t('virtual_kiosk.scan_qr_instruction') }}</p>

        <!-- Auto-scan handles the normal case; Shot stays as the manual
             retry for guests whose code needs a steadier frame. -->
        <div class="text-center">
            <button type="button" class="btn btn-kiosk-primary px-4" :disabled="!camera_ready" @click="shoot">
                {{ $t('virtual_kiosk.shot') }} {{ shot_count }}/1
            </button>
        </div>
    </b-modal>
</template>

<script>
import { createQrDecoder } from './kiosk_qr_decoder';

// How often a frame is pulled off the video for decoding. Fast enough to feel
// instant to the guest, slow enough not to pin the CPU on a kiosk tablet.
const SCAN_INTERVAL_MS = 250;

/**
 * The kiosk's QR scanner, shared by the start screen (where the pre-registered
 * QR button opens it straight away) and the check-in hub (its QR method).
 *
 * The parent calls open(); the first readable code closes the modal and comes
 * back as a `decoded` event. What the code means — email|pass, a reservation
 * number — is the parent's business: this component only reads it.
 *
 *   shown    the modal is opening (a guest is now busy with the camera)
 *   hidden   the modal is gone, camera stopped, decoded or dismissed
 *   decoded  the payload of the first QR code read
 */
export default {
    name: 'KioskQrScannerModal',
    props: {
        id: { type: String, default: 'kiosk-qr-scan-modal' },
    },
    data() {
        return {
            camera_ready: false,
            camera_error: '',
            camera_stream: null,
            shot_count: 0,

            // QR auto-scan
            scan_notice: '',
            scanned_value: '',
            qr_decoder: null,
            scan_timer: null,
            decoding: false,
        };
    },
    beforeDestroy() {
        this.stopCamera();
    },
    methods: {
        async open() {
            this.camera_error = '';
            this.scan_notice = '';
            this.scanned_value = '';
            this.shot_count = 0;
            this.$bvModal.show(this.id);

            try {
                // Front camera: the guest holds the QR code up facing the tablet.
                this.camera_stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user' },
                    audio: false,
                });
                await this.$nextTick();
                this.$refs.qrVideo.srcObject = this.camera_stream;
                this.camera_ready = true;
            } catch (e) {
                this.camera_error = this.$t('virtual_kiosk.camera_error');
                return;
            }

            this.qr_decoder = await createQrDecoder();
            // The modal may already have been dismissed while the decoder loaded.
            if (this.camera_ready) this.startScanLoop();
        },

        close() {
            this.$bvModal.hide(this.id);
        },

        onHidden() {
            this.stopCamera();
            this.$emit('hidden');
        },

        // Polls the live video until a QR code is readable — the guest never
        // has to press anything.
        startScanLoop() {
            this.stopScanLoop();
            this.scan_timer = setInterval(() => this.scanFrame(), SCAN_INTERVAL_MS);
        },

        stopScanLoop() {
            if (this.scan_timer) {
                clearInterval(this.scan_timer);
                this.scan_timer = null;
            }
        },

        // Reads the current frame. Returns the decoded payload, or null when
        // this frame holds no readable code.
        async decodeCurrentFrame() {
            const video = this.$refs.qrVideo;
            if (!video || !this.qr_decoder) return null;
            // HAVE_CURRENT_DATA — anything less has no pixels to read yet.
            if (video.readyState < 2 || !video.videoWidth) return null;

            try {
                return await this.qr_decoder(video);
            } catch (e) {
                return null;
            }
        },

        async scanFrame() {
            // Frames arrive faster than a decode takes; skip while one is running.
            if (this.decoding || this.scanned_value) return;
            this.decoding = true;

            try {
                const value = await this.decodeCurrentFrame();
                if (value) this.onQrDecoded(value);
            } finally {
                this.decoding = false;
            }
        },

        // Manual retry — same decode, but tells the guest when the frame was
        // unreadable instead of failing silently.
        async shoot() {
            if (!this.camera_ready || this.decoding) return;
            this.decoding = true;

            try {
                const value = await this.decodeCurrentFrame();
                if (value) {
                    this.onQrDecoded(value);
                } else {
                    this.scan_notice = this.$t('virtual_kiosk.qr_not_detected');
                }
            } finally {
                this.decoding = false;
            }
        },

        onQrDecoded(value) {
            if (this.scanned_value) return; // first read wins
            this.scanned_value = value;
            this.shot_count = 1;

            this.stopScanLoop();
            this.stopCamera();
            this.close();

            this.$emit('decoded', value);
        },

        stopCamera() {
            this.stopScanLoop();
            if (this.camera_stream) {
                this.camera_stream.getTracks().forEach((t) => t.stop());
                this.camera_stream = null;
            }
            this.camera_ready = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.btn-kiosk-primary {
    color: #fff;
    background-color: #1565d8;
    min-height: 48px;
    font-weight: 600;
    touch-action: manipulation;

    &:hover,
    &:active {
        color: #fff;
        background-color: #0f4fae;
    }
}

.scanner-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    background: #000;
    overflow: hidden;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .corner {
        position: absolute;
        width: 28px;
        height: 28px;
        border: 3px solid #ff2d2d;

        &.tl { top: 10px; left: 10px; border-right: none; border-bottom: none; }
        &.tr { top: 10px; right: 10px; border-left: none; border-bottom: none; }
        &.bl { bottom: 10px; left: 10px; border-right: none; border-top: none; }
        &.br { bottom: 10px; right: 10px; border-left: none; border-top: none; }
    }
}

.scanner-instruction {
    color: #9e9e9e;
    font-size: 14px;
}

.scanner-notice {
    color: #d9534f;
    font-size: 14px;
    font-weight: 600;
}
</style>
